<template>
  <v-card
    class="mcp-carousel"
    variant="outlined"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave"
    @focusin="handleFocusIn"
    @focusout="handleFocusOut"
  >
    <v-card-text class="mcp-carousel__content">
      <div class="mcp-carousel__header">
        <div class="mcp-carousel__copy">
          <div class="mcp-carousel__eyebrow">MCP HUB · DISCOVER</div>
          <h2 class="mcp-carousel__title">Plug your squad into the world</h2>
          <p class="mcp-carousel__subtitle">
            Install new servers, reconnect OAuth, or jump straight into tools from the MCP servers already available in
            your workspace.
          </p>
        </div>
        <div v-if="totalPages > 1" class="mcp-carousel__controls" role="group" aria-label="Carousel navigation">
          <v-btn icon variant="text" @click="goToPreviousPage" aria-label="Previous MCP servers">
            <v-icon>mdi-chevron-left</v-icon>
          </v-btn>
          <v-btn icon variant="text" @click="goToNextPage" aria-label="Next MCP servers">
            <v-icon>mdi-chevron-right</v-icon>
          </v-btn>
        </div>
      </div>

      <div v-if="carouselItems.length === 0" class="mcp-carousel__empty text-body-2 text-medium-emphasis">
        No MCP servers are available yet.
      </div>

      <div v-else class="mcp-carousel__track-frame">
        <div class="mcp-carousel__edge mcp-carousel__edge--left" aria-hidden="true" />
        <div
          ref="track"
          class="mcp-carousel__track"
          :style="trackStyle"
          @pointerdown="handlePointerInteraction"
        >
          <div v-for="item in carouselItems" :key="item.serverName" class="mcp-carousel__track-card">
            <McpServerCarouselCard :item="item" @primary-action="handlePrimaryAction" />
          </div>
        </div>
        <div class="mcp-carousel__edge mcp-carousel__edge--right" aria-hidden="true" />
      </div>

      <div v-if="carouselItems.length > 0" class="mcp-carousel__footer">
        <div v-if="totalPages > 1" class="mcp-carousel__dots" role="tablist" aria-label="Carousel pages">
          <button
            v-for="pageIndex in pages"
            :key="`carousel-page-${pageIndex}`"
            :class="['mcp-carousel__dot', { 'mcp-carousel__dot--active': pageIndex === page }]"
            :aria-label="`Go to page ${pageIndex + 1} of ${totalPages}`"
            :aria-selected="pageIndex === page"
            role="tab"
            type="button"
            @click="goToPage(pageIndex)"
          />
        </div>
        <div class="mcp-carousel__meta text-body-2 text-medium-emphasis">
          {{ footerMeta }}
        </div>
      </div>
    </v-card-text>
  </v-card>
</template>

<script lang="ts">
import McpServerCarouselCard from '@/components/mcp/McpServerCarouselCard.vue'
import { McpServerCarouselItem } from '@/utils/mcpCarousel'
import { nextTick } from 'vue'
import { Options, Vue } from 'vue-class-component'

@Options({
  components: {
    McpServerCarouselCard
  },
  props: {
    items: Array,
    perPage: {
      type: Number,
      default: 3
    },
    autoRotateMs: {
      type: Number,
      default: 5000
    },
    resumeDelayMs: {
      type: Number,
      default: 8000
    }
  },
  emits: ['select-server', 'install-server', 'connect-oauth'],
  watch: {
    items() {
      ;(this as McpServerCarousel).handleItemsChanged()
    }
  }
})
export default class McpServerCarousel extends Vue {
  items!: McpServerCarouselItem[]
  perPage!: number
  autoRotateMs!: number
  resumeDelayMs!: number

  page = 0
  currentItemsPerPage = 3
  hoverPaused = false
  focusPaused = false
  manualPaused = false
  rotateTimer: number | null = null
  resumeTimer: number | null = null
  scrollFrame: number | null = null

  private readonly handleWindowResize = () => this.updateItemsPerPage()
  private readonly handleTrackScroll = () => this.syncPageFromScroll()

  mounted(): void {
    this.updateItemsPerPage()
    this.trackElement?.addEventListener('scroll', this.handleTrackScroll, { passive: true })
    window.addEventListener('resize', this.handleWindowResize, { passive: true })
    this.handleItemsChanged()
  }

  beforeUnmount(): void {
    this.trackElement?.removeEventListener('scroll', this.handleTrackScroll)
    window.removeEventListener('resize', this.handleWindowResize)
    this.clearRotateTimer()
    this.clearResumeTimer()
    this.clearScrollFrame()
  }

  get trackElement(): HTMLElement | null {
    const refs = this.$refs as { track?: HTMLElement } | undefined
    return refs?.track || null
  }

  get carouselItems(): McpServerCarouselItem[] {
    return Array.isArray(this.items) ? this.items : []
  }

  get normalizedPerPage(): number {
    return Number.isFinite(this.perPage) && this.perPage > 0 ? Math.floor(this.perPage) : 3
  }

  get effectiveItemsPerPage(): number {
    return Number.isFinite(this.currentItemsPerPage) && this.currentItemsPerPage > 0
      ? Math.floor(this.currentItemsPerPage)
      : 1
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.carouselItems.length / this.effectiveItemsPerPage))
  }

  get pages(): number[] {
    return Array.from({ length: this.totalPages }, (_value, index) => index)
  }

  get isPaused(): boolean {
    return this.hoverPaused || this.focusPaused || this.manualPaused
  }

  get footerMeta(): string {
    if (this.carouselItems.length === 0) {
      return '0 servers'
    }

    const start = this.page * this.effectiveItemsPerPage + 1
    const end = Math.min(this.carouselItems.length, (this.page + 1) * this.effectiveItemsPerPage)
    return `${this.carouselItems.length} servers · showing ${start}-${end}`
  }

  get trackStyle(): Record<string, string> {
    return {
      '--mcp-carousel-items-per-page': String(this.effectiveItemsPerPage)
    }
  }

  handleItemsChanged(): void {
    this.syncPageWithinBounds()
    void nextTick(() => {
      this.scrollToPage(this.page, 'auto')
      this.refreshAutoRotate()
    })
  }

  handleMouseEnter(): void {
    this.hoverPaused = true
    this.refreshAutoRotate()
  }

  handleMouseLeave(): void {
    this.hoverPaused = false
    this.refreshAutoRotate()
  }

  handleFocusIn(): void {
    this.focusPaused = true
    this.refreshAutoRotate()
  }

  handleFocusOut(event: FocusEvent): void {
    const nextTarget = event.relatedTarget as Node | null
    if (!nextTarget || !(this.$el as HTMLElement).contains(nextTarget)) {
      this.focusPaused = false
      this.refreshAutoRotate()
    }
  }

  handlePointerInteraction(): void {
    this.bumpManualPause()
  }

  handlePrimaryAction(serverName: string): void {
    const item = this.carouselItems.find((candidate) => candidate.serverName === serverName)
    if (!item) {
      return
    }

    if (item.primaryActionKind === 'install') {
      this.$emit('install-server', serverName)
      return
    }

    if (item.primaryActionKind === 'connect-oauth') {
      this.$emit('connect-oauth', serverName)
      return
    }

    this.$emit('select-server', serverName)
  }

  goToPreviousPage(): void {
    this.bumpManualPause()
    this.scrollToPage(this.page - 1)
  }

  goToNextPage(): void {
    this.bumpManualPause()
    this.scrollToPage(this.page + 1)
  }

  goToPage(pageIndex: number): void {
    this.bumpManualPause()
    this.scrollToPage(pageIndex)
  }

  private updateItemsPerPage(): void {
    const nextItemsPerPage = this.resolveItemsPerPage()
    if (nextItemsPerPage === this.currentItemsPerPage) {
      return
    }

    this.currentItemsPerPage = nextItemsPerPage
    this.syncPageWithinBounds()
    void nextTick(() => {
      this.scrollToPage(this.page, 'auto')
      this.refreshAutoRotate()
    })
  }

  private resolveItemsPerPage(): number {
    if (typeof window === 'undefined') {
      return this.normalizedPerPage
    }

    if (window.innerWidth < 640) {
      return 1
    }

    if (window.innerWidth < 980) {
      return Math.min(2, this.normalizedPerPage)
    }

    return this.normalizedPerPage
  }

  private syncPageWithinBounds(): void {
    if (this.page >= this.totalPages) {
      this.page = this.totalPages - 1
    }

    if (this.page < 0) {
      this.page = 0
    }
  }

  private syncPageFromScroll(): void {
    this.clearScrollFrame()
    this.scrollFrame = window.requestAnimationFrame(() => {
      const track = this.trackElement
      if (!track) {
        return
      }

      const firstCard = track.querySelector('.mcp-carousel__track-card') as HTMLElement | null
      if (!firstCard) {
        return
      }

      const measurements = getTrackMeasurements(track, firstCard)
      const gap = measurements.gap
      const cardWidth = measurements.cardWidth
      const widthPerCard = cardWidth + gap
      if (widthPerCard <= 0) {
        return
      }

      const relativeScrollLeft = Math.max(0, track.scrollLeft - measurements.paddingLeft)
      const firstVisibleItemIndex = Math.round(relativeScrollLeft / widthPerCard)
      const nextPage = Math.min(
        this.totalPages - 1,
        Math.max(0, Math.floor(firstVisibleItemIndex / this.effectiveItemsPerPage))
      )

      this.page = nextPage
    })
  }

  private scrollToPage(targetPage: number, behavior: 'auto' | 'smooth' = 'smooth'): void {
    const track = this.trackElement
    const normalizedPage = normalizePageIndex(targetPage, this.totalPages)
    this.page = normalizedPage

    if (!track) {
      return
    }

    const firstCard = track.querySelector('.mcp-carousel__track-card') as HTMLElement | null
    if (!firstCard) {
      return
    }

    const measurements = getTrackMeasurements(track, firstCard)
    const left = measurements.paddingLeft + normalizedPage * this.effectiveItemsPerPage * (measurements.cardWidth + measurements.gap)

    track.scrollTo({
      left,
      behavior
    })
  }

  private refreshAutoRotate(): void {
    this.clearRotateTimer()

    if (this.isPaused || this.totalPages <= 1) {
      return
    }

    this.rotateTimer = window.setInterval(() => {
      this.scrollToPage(this.page + 1)
    }, this.autoRotateMs)
  }

  private bumpManualPause(): void {
    this.manualPaused = true
    this.refreshAutoRotate()
    this.clearResumeTimer()
    this.resumeTimer = window.setTimeout(() => {
      this.manualPaused = false
      this.refreshAutoRotate()
    }, this.resumeDelayMs)
  }

  private clearRotateTimer(): void {
    if (this.rotateTimer !== null) {
      window.clearInterval(this.rotateTimer)
      this.rotateTimer = null
    }
  }

  private clearResumeTimer(): void {
    if (this.resumeTimer !== null) {
      window.clearTimeout(this.resumeTimer)
      this.resumeTimer = null
    }
  }

  private clearScrollFrame(): void {
    if (this.scrollFrame !== null) {
      window.cancelAnimationFrame(this.scrollFrame)
      this.scrollFrame = null
    }
  }
}

function normalizePageIndex(targetPage: number, totalPages: number): number {
  if (totalPages <= 0) {
    return 0
  }

  return ((targetPage % totalPages) + totalPages) % totalPages
}

function getTrackMeasurements(track: HTMLElement, firstCard: HTMLElement): {
  gap: number
  cardWidth: number
  paddingLeft: number
} {
  const styles = window.getComputedStyle(track)

  return {
    gap: parseFloat(styles.columnGap || styles.gap || '0') || 0,
    cardWidth: firstCard.getBoundingClientRect().width,
    paddingLeft: parseFloat(styles.paddingLeft || '0') || 0
  }
}
</script>

<style scoped>
.mcp-carousel {
  border-radius: 20px;
  background:
    linear-gradient(135deg, rgba(var(--v-theme-primary), 0.06), transparent 35%),
    rgba(var(--v-theme-surface), 0.98);
}

.mcp-carousel__content {
  padding: 28px;
}

.mcp-carousel__header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 22px;
}

.mcp-carousel__copy {
  flex: 1;
  min-width: 0;
}

.mcp-carousel__eyebrow {
  margin-bottom: 8px;
  color: rgb(var(--v-theme-primary));
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.14em;
}

.mcp-carousel__title {
  margin: 0 0 8px;
  font-size: clamp(1.8rem, 2vw, 2.35rem);
  line-height: 1.1;
}

.mcp-carousel__subtitle {
  max-width: 760px;
  margin: 0;
  color: rgba(var(--v-theme-on-surface), 0.72);
  line-height: 1.6;
}

.mcp-carousel__controls {
  display: flex;
  gap: 6px;
}

.mcp-carousel__controls :deep(.v-btn) {
  border: 1px solid rgba(var(--v-theme-primary), 0.16);
}

.mcp-carousel__track-frame {
  position: relative;
}

.mcp-carousel__track {
  --mcp-carousel-gap: 16px;
  --mcp-carousel-side-padding: 10px;
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: calc((100% - (var(--mcp-carousel-gap) * (var(--mcp-carousel-items-per-page) - 1)) - (var(--mcp-carousel-side-padding) * 2)) / var(--mcp-carousel-items-per-page));
  column-gap: var(--mcp-carousel-gap);
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  scroll-padding-left: var(--mcp-carousel-side-padding);
  scroll-padding-right: var(--mcp-carousel-side-padding);
  scrollbar-width: none;
  padding: 4px var(--mcp-carousel-side-padding);
  box-sizing: border-box;
}

.mcp-carousel__track::-webkit-scrollbar {
  display: none;
}

.mcp-carousel__track-card {
  scroll-snap-align: start;
}

.mcp-carousel__edge {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 32px;
  pointer-events: none;
}

.mcp-carousel__edge--left {
  left: 0;
  background: linear-gradient(to right, rgba(var(--v-theme-surface), 1), rgba(var(--v-theme-surface), 0));
}

.mcp-carousel__edge--right {
  right: 0;
  background: linear-gradient(to left, rgba(var(--v-theme-surface), 1), rgba(var(--v-theme-surface), 0));
}

.mcp-carousel__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-top: 24px;
}

.mcp-carousel__dots {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.mcp-carousel__dot {
  width: 26px;
  height: 6px;
  padding: 0;
  border: none;
  border-radius: 999px;
  background: rgba(var(--v-theme-on-surface), 0.14);
  cursor: pointer;
  transition: background-color 0.18s ease, transform 0.18s ease, width 0.18s ease;
}

.mcp-carousel__dot--active {
  width: 36px;
  background: rgba(var(--v-theme-primary), 0.88);
}

.mcp-carousel__meta {
  text-align: right;
}

.mcp-carousel__empty {
  padding: 16px 0 4px;
}

@media (max-width: 900px) {
  .mcp-carousel__header,
  .mcp-carousel__footer {
    flex-direction: column;
    align-items: stretch;
  }

  .mcp-carousel__controls,
  .mcp-carousel__dots {
    justify-content: flex-start;
  }

  .mcp-carousel__meta {
    text-align: left;
  }
}

@media (max-width: 640px) {
  .mcp-carousel__content {
    padding: 20px;
  }

  .mcp-carousel__title {
    font-size: 1.6rem;
  }
}
</style>
